import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import { createAuthenticatedRequest } from "./AuthenticatedRequest";

// Compatibility adapter for BOB/Trans: keep setToken, storage keys and response-shaped errors.
let revision = 0;
const key = (name: string) => `${import.meta.env.VITE_APP_NAME}-${name}`;
const get = (name: string) => localStorage.getItem(key(name));
const save = (token: string, refreshToken: string, rights: unknown) => {
  localStorage.setItem(key("token"), token);
  localStorage.setItem(key("refreshToken"), refreshToken);
  localStorage.setItem(
    key("rights"),
    typeof rights === "string" ? rights : JSON.stringify(rights ?? {}),
  );
};
export function setToken(token: string, refreshToken: string, rights: unknown) {
  ++revision;
  save(token, refreshToken, rights);
}
interface LegacySession {
  token: string;
  refresh_token: string;
  rights?: unknown;
}
function isSession(data: unknown): data is LegacySession {
  if (typeof data !== "object" || data === null) return false;
  const value = data as Partial<LegacySession>;
  return (
    typeof value.token === "string" &&
    !!value.token &&
    typeof value.refresh_token === "string" &&
    !!value.refresh_token
  );
}
const client = createAuthenticatedRequest({
  baseURL: import.meta.env.VITE_APP_URL || "",
  strictPaths: false,
  // BOB and Trans historically had no default request timeout. Individual
  // requests can still opt into a timeout through their Axios configuration.
  timeout: 0,
  refreshBody: (token) => {
    const body = new URLSearchParams();
    body.set("refresh_token", token);
    return body;
  },
  session: {
    snapshot: () => ({
      revision,
      token: get("token"),
      refreshToken: get("refreshToken"),
    }),
    isResponse: isSession,
    accept: (data, expected) => {
      if (revision !== expected || !get("token") || !isSession(data))
        return false;
      save(data.token, data.refresh_token, data.rights);
      return true;
    },
    // Existing applications own their persisted login flag. Keep their error/logout flow.
    clear: () => {},
  },
});
export const transport = client.transport;
// Existing callers rely on untyped responses; new clients use the typed factory directly.
export default async function request<T = any>(
  options: AxiosRequestConfig,
  authenticated = true,
): Promise<T> {
  try {
    return await client.request<T>(options, authenticated);
  } catch (error) {
    throw axios.isAxiosError(error) ? (error.response ?? error) : error;
  }
}
